import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Empty, Statistic, Table, Tag, Typography,
} from 'antd';
import {
  ArrowDownOutlined, ArrowUpOutlined, MinusOutlined,
} from '@ant-design/icons';
import { SEGMENTS, getSegmentCount } from '../constants';
import SegmentSankey from './SegmentSankey';

const { Text } = Typography;

const BASELINE_LABELS = {
  cin7: 'Live (Cin7)',
  monday: 'Live (Monday)',
};

const baselineColumnTitle = (source) => (
  BASELINE_LABELS[source] || (source ? `Live (${source})` : 'Live standings')
);

const renderDelta = (delta, hasPrevious) => {
  if (!hasPrevious) return <Text type="secondary">—</Text>;
  if (delta > 0) {
    return (
      <Text style={{ color: '#52c41a' }}>
        <ArrowUpOutlined />
        {' '}
        {delta}
      </Text>
    );
  }
  if (delta < 0) {
    return (
      <Text style={{ color: '#cf1322' }}>
        <ArrowDownOutlined />
        {' '}
        {Math.abs(delta)}
      </Text>
    );
  }
  return (
    <Text type="secondary">
      <MinusOutlined />
      {' '}
      0
    </Text>
  );
};

const DistributionSummary = ({ summary }) => {
  const segments = summary?.segments ?? null;
  const previous = summary?.previous ?? null;
  const hasPrevious = previous != null && typeof previous === 'object';
  const moved = Array.isArray(summary?.moved) ? summary.moved : [];
  const transitions = Array.isArray(summary?.transitions) ? summary.transitions : [];
  const repTransitions = Array.isArray(summary?.repTransitions) ? summary.repTransitions : [];

  const rows = useMemo(() => SEGMENTS.map((segment) => {
    const current = getSegmentCount(segments, segment);
    const prev = hasPrevious ? getSegmentCount(previous, segment) : null;
    return {
      key: segment.key,
      label: segment.label,
      color: segment.color,
      current,
      previous: prev,
      delta: hasPrevious ? current - prev : null,
    };
  }), [segments, previous, hasPrevious]);

  if (!segments || typeof segments !== 'object') {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Segment distribution will appear once scoring completes."
      />
    );
  }

  const totalCustomers = summary?.totalCustomers ?? null;
  const changedCount = summary?.changedCount ?? null;
  const exact = !!summary?.changedCountExact;
  const baselineSource = summary?.baselineSource || null;

  return (
    <div className="segmentation-distribution">
      <div className="segmentation-stat-grid" style={{ marginBottom: 16 }}>
        <div className="segmentation-stat">
          <Statistic title="Total customers" value={totalCustomers ?? '—'} />
        </div>
        <div className="segmentation-stat">
          <Statistic
            title={exact ? 'Changed vs live (exact)' : 'Changed vs live (estimate)'}
            value={changedCount ?? '—'}
          />
        </div>
      </div>

      {!hasPrevious && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Live Cin7/Monday standings unavailable — deltas hidden for this run."
        />
      )}

      <Table
        size="middle"
        pagination={false}
        dataSource={rows}
        columns={[
          {
            title: 'Segment',
            dataIndex: 'label',
            key: 'label',
            render: (label, row) => <Tag color={row.color}>{label}</Tag>,
          },
          {
            title: 'This run',
            dataIndex: 'current',
            key: 'current',
            align: 'right',
            render: (value) => <Text strong>{value}</Text>,
          },
          {
            title: baselineColumnTitle(baselineSource),
            dataIndex: 'previous',
            key: 'previous',
            align: 'right',
            render: (value) => (value == null ? <Text type="secondary">—</Text> : value),
          },
          {
            title: 'Change',
            dataIndex: 'delta',
            key: 'delta',
            align: 'right',
            render: (delta, row) => renderDelta(delta, row.previous != null),
          },
        ]}
      />

      {transitions.length > 0 && (
        <SegmentSankey
          transitions={transitions}
          moved={moved}
          changedCount={changedCount ?? 0}
          repTransitions={repTransitions}
        />
      )}
    </div>
  );
};

DistributionSummary.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  summary: PropTypes.object,
};

DistributionSummary.defaultProps = {
  summary: null,
};

export default DistributionSummary;
